import { memo, useEffect, useState } from "react";
import { Button } from "antd";
import { BlockAdd } from "../../../../components/block/block";
import { Icon } from "../../../../components/icon/icon";
import ContentSearchModal from "../../../../components/content-search/content-search-modal";
import { ArticleThumbnail } from "../../../../components/content-search/thumbnail";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

/**
 * LF360-2030: "Add article" opens the content search — keyword, filters,
 * several at once — instead of a dropdown of every article's title. ⌘K / Ctrl+K
 * opens it too while the task is being edited.
 *
 * Tasks hold articles only, so the search offers no FitFacts here.
 */
export const PageListAdd = memo(({ attachedIds = [], onAdd, editMode, taskTitle }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!editMode) {return undefined;}

    const onKey = (e) => {
      // By the physical key as well: in a Cyrillic layout `key` is "л".
      if ((e.metaKey || e.ctrlKey) && (e.code === "KeyK" || e.key?.toLowerCase() === "k")) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [editMode]);

  if (!editMode) {return null;}

  return (
    <>
      <BlockAdd
        title={
          <span className="lf-task-articles__add">
            Add article <kbd>{isMac ? "⌘K" : "Ctrl+K"}</kbd>
          </span>
        }
        onClick={() => setOpen(true)}
      />
      {open && (
        <ContentSearchModal
          open={open}
          onClose={() => setOpen(false)}
          onAdd={(items) => {
            onAdd(items);
            setOpen(false);
          }}
          types={["articles"]}
          attachedIds={attachedIds}
          context={`Task · ${taskTitle || "New task"}`}
        />
      )}
    </>
  );
});

/** The task's articles: picture, title and category, as the design lists them. */
export const PageList = memo(({ pages, editMode, onRemove, onOpenPage }) => {
  if (!pages?.length) {return null;}

  return (
    <div className="lf-task-articles">
      <div className="lf-task-articles__head">
        <h4>Articles</h4>
        <span className="lf-task-articles__count">{pages.length}</span>
      </div>

      {pages.map((page) => (
        <div key={page.id} className="lf-task-articles__row">
          <div
            className="lf-task-articles__content"
            onClick={() => onOpenPage?.(page)}
            style={{ cursor: onOpenPage ? "pointer" : "default" }}
          >
            <ArticleThumbnail page={page} size={32} />
            <div className="lf-task-articles__text">
              <div className="lf-task-articles__title">{page.title}</div>
              <div className="lf-task-articles__category">
                {page.Category?.name || page.category?.name || "No category"}
              </div>
            </div>
          </div>

          {editMode && (
            <Button
              type="text"
              size="small"
              className="lf-task-articles__remove"
              icon={<Icon name={"delete"} />}
              aria-label={`Remove ${page.title}`}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(page.id);
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
});
